import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../logo.svg";

// Styled Components
const NavbarWrapper = styled(motion.nav)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2.5rem;
  background: #fff;
  backdrop-filter: blur(20px);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.05);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 0.8rem 1.5rem;
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  gap: 0.8rem;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const LogoImage = styled(motion.img)`
  height: 40px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
`;

const LogoText = styled(motion.span)`
  font-size: 1.6rem;
  font-weight: 800;
  background: linear-gradient(135deg, #507fbc, #71bda1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -0.5px;
`;

const NavLinks = styled(motion.ul)`
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;

  @media (max-width: 991px) {
    display: none; // Hide on mobile by default
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: #333;
  font-weight: 600;
  font-size: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;
  background: ${(props) => (props.active ? "rgba(80, 128, 188, 0.1)" : "transparent")};

  &::after {
    content: "";
    position: absolute;
    bottom: -2px;
    left: 50%;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #507fbc, #71bda1);
    transition: all 0.3s ease;
    transform: translateX(-50%);
  }

  &:hover {
    color: #507fbc;
    background: rgba(80, 128, 188, 0.05);

    &::after {
      width: 80%;
    }
  }
`;

const Dropdown = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const DropdownButton = styled.button`
  background: none;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  color: #444;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  width: 100%;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;

  span {
    display: flex;
    align-items: center;
  }

  svg {
    font-size: 0.8rem;
    transition: transform 0.3s ease;
    transform: ${({ open }) => (open ? "rotate(180deg)" : "rotate(0)")};
  }

  &:hover {
    color: #507fbc;
    background: rgba(80, 128, 188, 0.08);
  }
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  list-style: none;
  padding: 0.5rem;
  min-width: 220px;
  z-index: 1000;
  overflow: hidden;

  @media (max-width: 768px) {
    position: static;
    box-shadow: none;
    width: 100%;
    background: rgba(0, 0, 0, 0.02);
    border-radius: 0;
    padding: 0;
  }
`;

const DropdownItem = styled.li`
  margin: 0.2rem 0;
  border-radius: 8px;
  overflow: hidden;

  a {
    display: block;
    padding: 0.8rem 1rem;
    color: #333;
    text-decoration: none;
    font-weight: 500;
    transition: all 0.2s ease;
    border-left: 3px solid transparent;

    &:hover {
      background: rgba(80, 128, 188, 0.1);
      color: #507fbc;
      border-left: 3px solid #507fbc;
    }
  }
`;

const ContactButton = styled(Link)`
  text-decoration: none;
  color: #fff;
  font-weight: 600;
  font-size: 1rem;
  padding: 0.5rem 1.2rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  background: linear-gradient(90deg, #507fbc, #71bda1);
  box-shadow: 0 4px 15px rgba(80, 128, 188, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(80, 128, 188, 0.3);
  }
`;

const MobileMenuButton = styled(motion.button)`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: #507fbc;
  font-size: 1.5rem;

  @media (max-width: 991px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileNavLinks = styled(motion.ul)`
  display: none;
  flex-direction: column;
  gap: 1rem;
  list-style: none;
  margin: 0;
  padding: 1rem 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 999;
  text-align: center; /* Center-align mobile nav items */

  @media (max-width: 991px) {
    display: ${({ open }) => (open ? "flex" : "none")};
  }
`;

const MobileNavLink = styled(Link)`
  text-decoration: none;
  color: #333;
  font-weight: 600;
  font-size: 1rem;
  padding: 1rem 2rem;
  transition: all 0.3s ease;
  background: ${(props) => (props.active ? "rgba(80, 128, 188, 0.1)" : "transparent")};

  &:hover {
    background: rgba(80, 128, 188, 0.08);
    color: #507fbc;
  }
`;

const Navbar = () => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleNavLinkClick = () => {
    setMenuOpen(false);
    setDropdownOpen(false);
  };

  const toggleDropdown = (e) => {
    e.stopPropagation();
    setDropdownOpen(!dropdownOpen);
  };

  useEffect(() => {
    const closeDropdown = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("click", closeDropdown);
    return () => {
      document.removeEventListener("click", closeDropdown);
    };
  }, []);

  const dropdownVariants = {
    hidden: { opacity: 0, y: -10, height: 0 },
    visible: {
      opacity: 1,
      y: 0,
      height: "auto",
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      y: -10,
      height: 0,
      transition: {
        duration: 0.2,
        ease: "easeIn",
      },
    },
  };

  return (
    <NavbarWrapper>
      <LogoContainer to="/">
        <LogoImage src={logo} alt="Logo" />
        <LogoText>DocGenesys</LogoText>
      </LogoContainer>

      <MobileMenuButton onClick={() => setMenuOpen(!menuOpen)}>
        {menuOpen ? "✖" : "☰"}
      </MobileMenuButton>

      {/* Desktop Nav Links */}
      <NavLinks>
        <li>
          <NavLink to="/" active={location.pathname === "/"} onClick={handleNavLinkClick}>
            Home
          </NavLink>
        </li>
        <li>
          <NavLink to="/key-features" active={location.pathname === "/key-features"} onClick={handleNavLinkClick}>
            Features
          </NavLink>
        </li>
        <li>
          <NavLink to="/use-cases" active={location.pathname === "/use-cases"} onClick={handleNavLinkClick}>
            Use Cases
          </NavLink>
        </li>
        <li>
          <NavLink to="/comparison" active={location.pathname === "/comparison"} onClick={handleNavLinkClick}>
            Compare
          </NavLink>
        </li>
        <li>
          <Dropdown ref={dropdownRef}>
            <DropdownButton open={dropdownOpen} onClick={toggleDropdown}>
              <span>Security</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </DropdownButton>

            <AnimatePresence>
              {dropdownOpen && (
                <DropdownMenu
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={dropdownVariants}
                >
                  <DropdownItem>
                    <NavLink to="/role-based-access" onClick={handleNavLinkClick}>
                      Role-Based Access
                    </NavLink>
                  </DropdownItem>
                  <DropdownItem>
                    <NavLink to="/secure-data-transfer" onClick={handleNavLinkClick}>
                      Secure Transfer
                    </NavLink>
                  </DropdownItem>
                </DropdownMenu>
              )}
            </AnimatePresence>
          </Dropdown>
        </li>
        <li>
          <NavLink to="/faq" active={location.pathname === "/faq"} onClick={handleNavLinkClick}>
            FAQ
          </NavLink>
        </li>
        <a href="https://sandbox.docgenesys.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            textDecoration: "none",
            color: "#333",
            fontWeight: "600",
            fontSize: "1rem",
            padding: "0.5rem 1rem",
            borderRadius: "8px",
          }}
        >
        Login
        </a>
        <li>
          <ContactButton to="/contact" onClick={handleNavLinkClick}>
            Contact
          </ContactButton>
        </li>
      </NavLinks>
          
      {/* Mobile Nav Links */}
      <AnimatePresence>
        {menuOpen && (
          <MobileNavLinks  open={menuOpen}>
            <li>
              <MobileNavLink to="/" active={location.pathname === "/"} onClick={handleNavLinkClick}>
                Home
              </MobileNavLink>
            </li>
            <li>
              <MobileNavLink to="/key-features" active={location.pathname === "/key-features"} onClick={handleNavLinkClick}>
                Features
              </MobileNavLink>
            </li>
            <li>
              <MobileNavLink to="/use-cases" active={location.pathname === "/use-cases"} onClick={handleNavLinkClick}>
                Use Cases
              </MobileNavLink>
            </li>
            <li>
              <MobileNavLink to="/comparison" active={location.pathname === "/comparison"} onClick={handleNavLinkClick}>
                Compare
              </MobileNavLink>
            </li>
            <li>
              <MobileNavLink to="/role-based-access" onClick={handleNavLinkClick}>
                Role-Based Access
              </MobileNavLink>
            </li>
            <li>
              <MobileNavLink to="/secure-data-transfer" onClick={handleNavLinkClick}>
                Secure Transfer
              </MobileNavLink>
            </li>
            <li>
              <MobileNavLink to="/faq" active={location.pathname === "/faq"} onClick={handleNavLinkClick}>
                FAQ
              </MobileNavLink>
            </li>
            <a href="https://sandbox.docgenesys.com"
          style={{
            textDecoration: "none",
            color: "#333",
            fontWeight: "600",
            fontSize: "1rem",
            padding: "0.5rem 1rem",
            borderRadius: "8px",
          }}
        >
        Login
        </a>
            <li>
              <ContactButton to="/contact" onClick={handleNavLinkClick}>
                Contact
              </ContactButton>
            </li>
          </MobileNavLinks>
        )}
      </AnimatePresence>
    </NavbarWrapper>
  );
};

export default Navbar;